// BotContainer.tsx - FIXED: CREATE mode now uses same 2-tab system as EDIT
// 🎯 MAJOR FIX: 
// - CREATE mode now properly split into 2 tabs like EDIT mode
// - Consistent UX between create and edit experiences  
// - Proper form section control using BotForm props
// - Fixed TypeScript errors with BotList component props

import React, { useState, useEffect } from 'react';
import Header from "../../components/header";
import BotForm from './BotForm';
import BotList from './BotList';
import BotMetrics from './BotMetrics';
import BotFlowBuilder from './flow/BotFlowBuilder'; // NEW: Visual flow builder
import ErrorBoundary from '../../components/ErrorBoundary';
import UltimatePlanInfo from '../../components/UltimatePlanInfo/UltimatePlanInfo';
import BackToTop from './components/BackToTop';
// Removed Container import - using custom full-width containers
import logger from '../../utils/logger';
import { createBot, getBotById, updateBot } from '../../services/botService'; // ✅ API INTEGRATION
import toast from 'react-hot-toast';

// 🎯 TYPES: View management (tabs removed - single page flow builder)
type ViewMode = 'list' | 'create' | 'edit' | 'metrics';

const BotContainer: React.FC = () => {
  logger.log('🏠 BotContainer: Component rendered - FIXED CREATE MODE VERSION');

  // 🔥 CORE STATE: View management
  const [currentView, setCurrentView] = useState<ViewMode>('list');
  const [editingBotId, setEditingBotId] = useState<string | null>(null);
  
  // 🔥 FORM STATE: Tracking for container-level actions
  const [isFormValid, setIsFormValid] = useState(true);
  const [isFormSaving, setIsFormSaving] = useState(false);

  // 🔥 PLAN STATE: Ultimate plan check
  const [isUltimate, setIsUltimate] = useState<boolean | null>(null);

  // 🔥 BOT DATA STATE: For edit mode
  const [editingBotData, setEditingBotData] = useState<any>(null);
  const [isLoadingBot, setIsLoadingBot] = useState(false);

  logger.log('📊 BotContainer: Current state:', {
    currentView,
    editingBotId,
    isFormValid,
    isFormSaving,
    isUltimate
  });

  // 🔥 Ultimate plan check
  useEffect(() => {
    try {
      logger.log('🔍 BotContainer: Checking ultimate plan status');
      const localData = window.localStorage?.getItem("userData");
      if (localData) {
        const userData = JSON.parse(localData);
        const planType = userData?.subscriptionPlanType;
        const isUltimatePlan = typeof planType === 'string' && planType.toLowerCase() === "ultimate";

        logger.log('📋 BotContainer: Plan check result:', {
          planType,
          isUltimatePlan,
          userDataExists: !!userData
        });

        setIsUltimate(isUltimatePlan);
      } else {
        logger.warn('❌ BotContainer: No userData found in localStorage');
        setIsUltimate(false);
      }
    } catch (error) {
      console.error('❌ BotContainer: Error checking ultimate plan:', error);
      setIsUltimate(false);
    }
  }, []);

  // 🔥 Guard: redirect to list for invalid states (avoids setState during render)
  useEffect(() => {
    if ((currentView === 'edit' || currentView === 'metrics') && !editingBotId) {
      logger.warn('⚠️ BotContainer: No botId in edit/metrics mode — redirecting to list');
      setCurrentView('list');
    }
    if (currentView !== 'list' && currentView !== 'create' && currentView !== 'edit' && currentView !== 'metrics') {
      logger.warn('⚠️ BotContainer: Unknown view mode — redirecting to list');
      setCurrentView('list');
    }
  }, [currentView, editingBotId]);

  // 🔥 Load bot data when entering edit mode
  useEffect(() => {
    const loadBotData = async () => {
      if (currentView === 'edit' && editingBotId) {
        logger.log('📥 BotContainer: Loading bot data for edit', editingBotId);
        setIsLoadingBot(true);

        try {
          const response = await getBotById(editingBotId);
          const bot = response.data;

          logger.log('✅ BotContainer: Bot data loaded', {
            id: bot._id,
            name: bot.name,
            groupsCount: bot.groups?.length,
            keywords: bot.triggerCondition?.keywords
          });

          setEditingBotData(bot);
        } catch (error: any) {
          console.error('❌ BotContainer: Failed to load bot data', error);
          toast.error('Failed to load bot data');
          setCurrentView('list');
        } finally {
          setIsLoadingBot(false);
        }
      }
    };

    loadBotData();
  }, [currentView, editingBotId]);

  // 🔥 NAVIGATION HANDLERS
  
  // Handle navigation to create new bot
  const handleCreateNew = () => {
    logger.log('➕ BotContainer: Navigating to create new bot');
    setCurrentView('create');
    setEditingBotId(null);
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle navigation to edit existing bot
  const handleEditBot = (botId: string) => {
    logger.log('✏️ BotContainer: Navigating to edit bot:', botId);
    setCurrentView('edit');
    setEditingBotId(botId);
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle successful form submission
  const handleFormSuccess = () => {
    logger.log('✅ BotContainer: Form submitted successfully, returning to list');
    setCurrentView('list');
    setEditingBotId(null);
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Handle form cancellation
  const handleFormCancel = () => {
    logger.log('❌ BotContainer: Form cancelled, returning to list');
    setCurrentView('list');
    setEditingBotId(null);
    setIsFormValid(true);
    setIsFormSaving(false);
  };

  // Removed: Tab switching - now using single-page flow builder

  // 🔥 Navigate to metrics (separate view, not tab)
  const handleViewMetrics = () => {
    logger.log('📊 BotContainer: Navigating to metrics view');
    setCurrentView('metrics');
  };

  // Handle form state updates from BotForm component
  const handleFormStateChange = (isValid: boolean, isSaving: boolean) => {
    logger.log('📊 BotContainer: Form state changed:', { isValid, isSaving });
    setIsFormValid(isValid);
    setIsFormSaving(isSaving);
  };

  // Handle form submission from container level
  const handleContainerFormSubmit = () => {
    logger.log('🚀 BotContainer: Triggering form submission from container');
    
    const form = document.getElementById('botForm') as HTMLFormElement;
    if (form) {
      const submitEvent = new Event('submit', { 
        cancelable: true, 
        bubbles: true 
      });
      form.dispatchEvent(submitEvent);
    } else {
      console.error('❌ BotContainer: Form element not found');
    }
  };

  // 🔥 RENDER LOGIC: Main view rendering with UNIFIED tab system

  const renderCurrentView = () => {
    logger.log('🔄 BotContainer: Rendering view:', currentView);

    switch (currentView) {
      
      // 📝 LIST MODE: Standard bot list
      // Note: Metrics can be accessed by editing a bot and clicking the Analytics button
      case 'list':
        logger.log('📋 BotContainer: Rendering bot list');
        return (
          <BotList 
            onCreateNew={handleCreateNew}
            onEditBot={handleEditBot}
          />
        );

      // 📝 CREATE MODE: Single-page visual flow builder
      case 'create':
        logger.log('📝 BotContainer: Rendering create mode with visual flow builder');
        return (
          <>
            <Header />
            <ErrorBoundary componentName="BotFlowBuilder">
            <BotFlowBuilder
              initialGroups={[]}
              initialBotData={{ name: '', description: '', isActive: true }}
              onBack={handleFormCancel}
              onSave={async (botData) => {
                logger.log('💾 BotContainer: Creating bot', botData);

                try {
                  // Transform data from flow builder format to API format
                  const payload = {
                    name: botData.name,
                    description: botData.description || '',
                    objective: 'lead_generation',
                    isExistingLeadBot: botData.isExistingLeadBot || false,
                    triggerCondition: {
                      type: 'keyword' as const,
                      keywords: botData.triggerCondition.keywords,
                      requireAllKeywords: false,
                    },
                    status: (botData.isActive ? 'active' : 'inactive') as 'active' | 'inactive',
                    groups: botData.groups,
                  };

                  logger.log('📤 BotContainer: Sending to API', payload);

                  const createdBot = await createBot(payload);

                  logger.log('✅ BotContainer: Bot created successfully', createdBot);
                  toast.success(`Bot "${botData.name}" created successfully! 🎉`);

                  handleFormSuccess();
                } catch (error: any) {
                  console.error('❌ BotContainer: Failed to create bot', error);
                  const errorMsg = error.response?.data?.message || 'Failed to create bot';
                  toast.error(errorMsg);
                  throw error; // Re-throw so BotFlowBuilder can handle it
                }
              }}
              isEdit={false}
            />
            </ErrorBoundary>
          </>
        );

      // ✏️ EDIT MODE: Single-page visual flow builder
      case 'edit':
        logger.log('✏️ BotContainer: Rendering edit mode with visual flow builder');

        if (!editingBotId) {
          return null; // useEffect guard handles redirect
        }

        // Show loading state while fetching bot data
        if (isLoadingBot || !editingBotData) {
          return (
            <>
              <Header />
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '60vh',
                flexDirection: 'column',
                gap: '16px'
              }}>
                <div style={{ fontSize: '48px' }}>⏳</div>
                <div style={{ fontSize: '18px', color: '#6b7280' }}>Loading bot data...</div>
              </div>
            </>
          );
        }

        return (
          <>
            <Header />
            <ErrorBoundary componentName="BotFlowBuilder">
            <BotFlowBuilder
              botId={editingBotId}
              initialGroups={editingBotData.groups || []}
              initialBotData={{
                name: editingBotData.name,
                description: editingBotData.description || '',
                isActive: editingBotData.status === 'active',
                isExistingLeadBot: editingBotData.isExistingLeadBot || false,
                triggerCondition: editingBotData.triggerCondition,
              }}
              onBack={handleFormCancel}
              onAnalytics={handleViewMetrics}
              onSave={async (botData) => {
                logger.log('💾 BotContainer: Updating bot', botData);

                try {
                  // Transform data from flow builder format to API format
                  const payload = {
                    name: botData.name,
                    description: botData.description || '',
                    objective: 'lead_generation',
                    isExistingLeadBot: botData.isExistingLeadBot || false,
                    triggerCondition: {
                      type: 'keyword' as const,
                      keywords: botData.triggerCondition.keywords,
                      requireAllKeywords: false,
                    },
                    status: (botData.isActive ? 'active' : 'inactive') as 'active' | 'inactive',
                    groups: botData.groups,
                  };

                  logger.log('📤 BotContainer: Sending update to API', payload);

                  const updatedBot = await updateBot(editingBotId, payload);

                  logger.log('✅ BotContainer: Bot updated successfully', updatedBot);
                  toast.success(`Bot "${botData.name}" updated successfully! 🎉`);

                  handleFormSuccess();
                } catch (error: any) {
                  console.error('❌ BotContainer: Failed to update bot', error);
                  const errorMsg = error.response?.data?.message || 'Failed to update bot';
                  toast.error(errorMsg);
                  throw error; // Re-throw so BotFlowBuilder can handle it
                }
              }}
              onAutoSave={async (botData) => {
                // Silent auto-save - no toast, no navigation
                logger.log('💾 BotContainer: Auto-saving bot', botData);

                try {
                  const payload = {
                    name: botData.name,
                    description: botData.description || '',
                    objective: 'lead_generation',
                    isExistingLeadBot: botData.isExistingLeadBot || false,
                    triggerCondition: {
                      type: 'keyword' as const,
                      keywords: botData.triggerCondition.keywords,
                      requireAllKeywords: false,
                    },
                    status: (botData.isActive ? 'active' : 'inactive') as 'active' | 'inactive',
                    groups: botData.groups,
                  };

                  await updateBot(editingBotId, payload);
                  logger.log('✅ BotContainer: Auto-save successful');
                } catch (error: any) {
                  console.error('❌ BotContainer: Auto-save failed', error);
                  throw error; // Re-throw so BotFlowBuilder can display error
                }
              }}
              isEdit={true}
            />
            </ErrorBoundary>
          </>
        );

      // 📊 METRICS MODE: Separate metrics view (unchanged)
      case 'metrics':
        logger.log('📊 BotContainer: Rendering metrics view');
        if (!editingBotId) {
          return null; // useEffect guard handles redirect
        }
        return (
          <div id="main" className="main">
            <Header />
            <div style={{ width: '100%', padding: '24px', boxSizing: 'border-box' }}>
              <div style={{
                background: 'white',
                borderRadius: '12px',
                marginBottom: '24px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                overflow: 'hidden'
              }}>
                {/* Metrics Header */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '24px 32px',
                  borderBottom: '1px solid #f3f4f6'
                }}>
                  <div>
                    <h1 style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '28px',
                      fontWeight: '700',
                      color: '#111827',
                      margin: '0 0 8px 0'
                    }}>
                      📊 Bot Analytics
                    </h1>
                    <p style={{
                      fontFamily: 'Gilroy, sans-serif',
                      fontSize: '14px',
                      color: '#6b7280',
                      margin: '0'
                    }}>
                      Performance metrics and session history
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setCurrentView('edit');
                    }}
                    style={{
                      background: 'linear-gradient(135deg, #3FAEFD 0%, #2563eb 100%)',
                      color: 'white',
                      border: 'none',
                      padding: '12px 24px',
                      borderRadius: '8px',
                      fontFamily: 'Gilroy, sans-serif',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    ← Back to Edit
                  </button>
                </div>
              </div>
              <ErrorBoundary componentName="BotMetrics">
                <BotMetrics botId={editingBotId} />
              </ErrorBoundary>
            </div>
          </div>
        );

      // 🏠 DEFAULT: Back to list (fallback)
      default:
        logger.warn('❌ BotContainer: Unknown view mode:', currentView, '- redirecting to list');
        return null; // useEffect guard or next render will handle it
    }
  };

  // 🔥 ULTIMATE PLAN CHECK: Show upgrade notice if needed
  if (isUltimate === false) {
    logger.log('🔒 BotContainer: Showing Ultimate plan upgrade notice');
    return <UltimatePlanInfo />;
  }

  // 🔄 LOADING STATE: While checking plan
  if (isUltimate === null) {
    logger.log('⏳ BotContainer: Loading...');
    return <div>Loading...</div>;
  }

  // 🎨 RENDER: Main component
  return (
    <>
      {renderCurrentView()}
      {(currentView === 'create' || currentView === 'edit') && <BackToTop />}
    </>
  );
};

export default BotContainer;
